import React, { useState, useRef, useEffect } from 'react';
import './MultiSelectDropdown.css';

const MultiSelectDropdown = ({
  label = 'Select Items',
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select an option',
  valueKey = 'id', // e.g., 'idIngrediente'
  displayKeys = 'name' // or ['name', 'price']
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState(1);

  // Build a display string from one or more keys
  const getDisplay = (item) => {
    if (!item) return '';
    if (Array.isArray(displayKeys)) {
      return displayKeys.map((key) => item[key]).join(' - ');
    }
    return item[displayKeys];
  };

  // Check if an option is selected by valueKey
  const isSelected = (option) =>
    selected.some((sel) => sel[valueKey] === option[valueKey]);

  const toggleOption = (option) => {
    if (isSelected(option)) {
      onChange(selected.filter((item) => item[valueKey] !== option[valueKey]));
    } else {
      // when adding, default qty to 1
      onChange([...selected, { ...option, qty: 1 }]);
    }
  };

  const removeOption = (option) => {
    onChange(selected.filter((item) => item[valueKey] !== option[valueKey]));
  };

  const startEditQuantity = (option, e) => {
    e.stopPropagation();
    setEditingId(option[valueKey]);
    setEditingValue(option.qty || 1);
    setIsOpen(false);
  };

  const commitQuantity = (option) => {
    const q = parseInt(editingValue) || 1;
    const updated = selected.map(s => s[valueKey] === option[valueKey] ? { ...s, qty: q } : s);
    onChange(updated);
    setEditingId(null);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <fieldset className="dropdown-fieldset">
      <div className="dropdown-container" ref={dropdownRef}>
        {label && <legend className="dropdown-label">{label}</legend>}

        <div
          className="dropdown-box"
          onClick={(e) => {
            if (!e.target.classList.contains('dropdown-qty-input')) {
              setIsOpen((prev) => !prev);
            }
          }}
        >
          {selected.length === 0 ? (
            <span className="dropdown-placeholder">{placeholder}</span>
          ) : (
            selected.map((option) => (
              <span key={option[valueKey]} className="dropdown-tag">
                {editingId === option[valueKey] ? (
                  <input className='dropdown-qty-input' placeholder='Cantidad' type="number" value={editingValue} onChange={(e) => setEditingValue(e.target.value)} onBlur={() => commitQuantity(option)} autoFocus />
                ) : (
                    <span
                      onClick={(e) => startEditQuantity(option, e)}
                      role="button"
                      tabIndex={0}
                    >
                      {getDisplay(option)} {option.qty > 1 ? 'x' + option.qty : ''}
                    </span>
                )}
                <button
                  className="dropdown-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(option);
                  }}
                >
                  &times;
                </button>
              </span>
            ))
          )}
          <span className="dropdown-caret">&#x25BC;</span>
        </div>

        {isOpen && (
          <div className="dropdown-options">
            {options
              .filter((option) => !isSelected(option))
              .map((option) => (
                <div
                  key={option[valueKey]}
                  className="dropdown-option"
                  onClick={() => toggleOption(option)}
                >
                  {getDisplay(option)}
                </div>
              ))}
          </div>
        )}
      </div>
    </fieldset>
  );
};

export default MultiSelectDropdown;
